import { GetStaticPaths, GetStaticProps } from 'next';
import Head from 'next/head';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useSession } from 'next-auth/react';
import styled from 'styled-components';

import theme from '../../../assets/theme';
import PhonicsSlidePreview from '../../../components/_organisms/PhonicsSlidePreview';
import LandingPageLayout from '../../../components/layouts/LandingPageLayout';
import useAuthGuardPrompt from '../../../hooks/useAuthGuardPrompt';
import { DefaultLocale } from '../../../providers/tolgee';
import { getBaseUrl } from '../../../utils/client';

// Stable og:image URL served from /public (see hub page comment).
const OG_IMAGE_PATH = '/phonics-wizard-og.png';
import {
  CURRICULA,
  CURRICULUM_MARKETING,
  CurriculumEntry,
  CurriculumMarketing,
  extractRuleFromValue,
  formatGradeLabel,
  getAdjacentObjectives,
  getCurriculumBySlug,
  getObjectiveById,
  getObjectivesForCurriculum,
  LandingObjective,
  parseObjectiveSlug,
} from '../../../utils/phonicsLandingPages';

const PHONICS_CARD_ID = 'activity-phonics-wizard';

/**
 * Build the post-signup / post-login redirect URL for the "Use this lesson"
 * CTAs. We route through `/` so the index-page session-aware redirect can
 * substitute the user's real tenant id; the allowlisted forwarded params
 * (see `pages/index.tsx` HOME_FORWARDED_QUERY_PARAMS) carry through into
 * `/{tenantId}/home`, where the Phonics Wizard form picks them up and
 * pre-fills curriculum + grade + objective so the user only has to click
 * Generate.
 */
function buildLessonRedirect(curriculumSlug: string, objectiveSlug: string): string {
  const params = new URLSearchParams({
    selectedCardId: PHONICS_CARD_ID,
    phonicsCurriculum: curriculumSlug,
    phonicsObjective: objectiveSlug,
  });
  return `/?${params.toString()}`;
}

const LESSON_DURATION_LABEL = '~10 min';

type SerializableObjective = Omit<LandingObjective, 'unitNumber'> & {
  unitNumber: number | null;
};

type AdjacentLink = {
  /** Curriculum slug or full target-sound slug, suffixed onto /phonics-practice/. */
  href: string;
  /** Optional small location line ("K · Unit 3 · Lesson 11"); omitted for fallbacks. */
  location: string | null;
  /** Title shown in big text on the card. */
  title: string;
};

interface Props {
  curriculum: CurriculumEntry;
  marketing: CurriculumMarketing;
  objective: SerializableObjective;
  rule: string;
  prev: AdjacentLink;
  next: AdjacentLink;
}

const ObjectivePhonicsPage = ({
  curriculum,
  marketing,
  objective,
  rule,
  prev,
  next,
}: Props) => {
  // If the user is already authenticated, jump straight to the
  // pre-filled Phonics Wizard. The index page's session-aware redirect
  // picks up the tenant and lands them on `/${activeTenantId}/home` with
  // `selectedCardId` + `phonicsCurriculum` + `phonicsObjective` carried
  // through, so the form opens with everything filled in. Anonymous
  // visitors get the shared login/signup modal — same callback.
  const { status: sessionStatus } = useSession();
  const router = useRouter();
  const lessonRedirect = buildLessonRedirect(curriculum.slug, objective.slug);
  const { authGuardComponent, showAuthGuard } = useAuthGuardPrompt(
    undefined,
    undefined,
    undefined,
    lessonRedirect,
  );
  /**
   * Intercept anonymous clicks to open the auth modal, but otherwise let
   * the underlying `<a href>` navigate normally so the CTA renders as a
   * real link (crawlers can index it, right-click → open in new tab works,
   * etc.). Modifier keys / middle-click also bypass the intercept.
   *
   * While next-auth is still resolving the session (`status === 'loading'`),
   * fall through to the default `<a href>` navigation. The `/` index page
   * does its own server-side session check and either redirects logged-in
   * users into `/{tenantId}/home` or renders the marketing landing — so
   * we never wrongly open the auth modal for an in-flight signed-in
   * session.
   */
  const handleUseThisLesson = (e: React.MouseEvent) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) {
      return;
    }
    if (sessionStatus === 'loading') {
      return;
    }
    if (sessionStatus === 'authenticated') {
      e.preventDefault();
      router.push(lessonRedirect);
      return;
    }
    e.preventDefault();
    showAuthGuard();
  };

  const gradeLabel = formatGradeLabel(objective.grade);
  const unitLessonLabel =
    objective.unitNumber !== null && objective.unitNumber !== undefined
      ? `${gradeShortLabel(objective.grade)} · Unit ${objective.unitNumber} · Lesson ${
          objective.lessonNumber
        }`
      : `${gradeShortLabel(objective.grade)} · Lesson ${objective.lessonNumber}`;

  // Lead both titles with `<sound> phonics practice` so Google's
  // "phonics practice" + sound queries match. SERP listing + OG preview
  // both end with the brand.
  const seoTitle = `${rule} phonics practice · ${
    curriculum.displayName
  } ${gradeShortLabel(objective.grade)} | Curipod`;
  const ogTitle = `${rule} phonics practice — ${curriculum.displayName} | Curipod`;
  const exampleSentence =
    objective.exampleWords && objective.exampleWords.length > 0
      ? ` Example words: ${objective.exampleWords.join(', ')}.`
      : '';
  // Description template: "Whole-class Phonics Practice on the target sound
  // <sound> with instant feedback. Example words: <a, b, c>. <Curriculum
  // location>." Curriculum location reads as `{curriculum} · {grade · unit ·
  // lesson}` so the SERP snippet ties the sound to the scope-and-sequence.
  const seoDescription = `Whole-class Phonics Practice on the target sound ${rule} with instant feedback.${exampleSentence} ${curriculum.displayName} · ${unitLessonLabel}.`;

  // Structured data — emit a LearningResource that targets this objective,
  // a BreadcrumbList so Google can render the trail in the SERP listing,
  // and a HowTo for the 4-step "How it runs" section. All three are
  // hand-rolled JSON-LD so we can keep dependencies thin.
  const baseUrl = getBaseUrl();
  const curriculumUrl = `${baseUrl}/phonics-practice/${curriculum.slug}`;
  const lessonUrl = `${curriculumUrl}/${objective.slug}`;
  const learningResource = {
    '@context': 'https://schema.org',
    '@type': 'LearningResource',
    name: `${rule} phonics practice`,
    description: seoDescription,
    url: lessonUrl,
    inLanguage: 'en-US',
    learningResourceType: 'Whole-class activity',
    teaches: `Phonics — target sound ${rule}`,
    educationalAlignment: {
      '@type': 'AlignmentObject',
      alignmentType: 'educationalSubject',
      educationalFramework: curriculum.displayName,
      targetName: unitLessonLabel,
    },
    audience: {
      '@type': 'EducationalAudience',
      educationalRole: 'teacher',
    },
    typicalAgeRange: '5-11',
    publisher: { '@type': 'Organization', name: 'Curipod', url: 'https://curipod.com' },
  };
  const breadcrumbList = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Phonics Practice',
        item: `${baseUrl}/phonics-practice`,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: curriculum.displayName,
        item: curriculumUrl,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: `${rule} phonics practice`,
        item: lessonUrl,
      },
    ],
  };
  const howTo = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: `How to run ${rule} phonics practice with your class`,
    description: `Four-step whole-class phonics activity that drops in after ${curriculum.displayName}'s direct instruction on ${rule}.`,
    totalTime: 'PT10M',
    step: [
      {
        '@type': 'HowToStep',
        position: 1,
        name: 'Hear the sound',
        text: `You introduce ${rule} from the ${curriculum.displayName} lesson. Students listen and identify.`,
      },
      {
        '@type': 'HowToStep',
        position: 2,
        name: 'Spell & get feedback',
        text: `Every student types words with ${rule}. Each one gets instant feedback and a score.`,
      },
      {
        '@type': 'HowToStep',
        position: 3,
        name: 'Read aloud',
        text: 'Students share their words with a partner or the class.',
      },
      {
        '@type': 'HowToStep',
        position: 4,
        name: 'Learn & try again',
        text: 'You see who got it. Students hear new words from peers — then try again.',
      },
    ],
  };

  return (
    <LandingPageLayout
      title={seoTitle}
      ogTitle={ogTitle}
      subTitle={seoDescription}
      imageUrl={`${baseUrl}${OG_IMAGE_PATH}`}
      disableHeadline
    >
      <Head>
        <meta name="robots" content="index, follow" />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(learningResource) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbList) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(howTo) }}
        />
      </Head>
      <LessonHero>
        <LessonHeroText>
          <HeroMeta>
            <Link href={`/phonics-practice/${curriculum.slug}`} passHref>
              <a>{curriculum.displayName}</a>
            </Link>
            <span aria-hidden="true">·</span>
            <Link href={`/phonics-practice/${curriculum.slug}#browse`} passHref>
              <a>{unitLessonLabel}</a>
            </Link>
          </HeroMeta>
          <HeroTags>
            <Tag $variant="grade">{gradeLabel}</Tag>
            <Tag>
              <ClockDot />
              {LESSON_DURATION_LABEL}
            </Tag>
          </HeroTags>
          <HeroH1>
            {rule} <HeroH1Suffix>phonics practice</HeroH1Suffix>
          </HeroH1>
          <HeroLede>
            Whole-class phonics practice for {curriculum.displayName} {unitLessonLabel}.
            Every student spells words with <strong>{rule}</strong> and gets instant
            feedback — right after your direct instruction.
          </HeroLede>
          <UseButton as="a" href={lessonRedirect} onClick={handleUseThisLesson}>
            <PlayDot>▶</PlayDot>Use this lesson in Curipod
          </UseButton>
          <Facts>
            <Fact>
              <FactKey>Target sound</FactKey>
              <FactValue>{rule}</FactValue>
            </Fact>
            <Fact>
              <FactKey>Grade</FactKey>
              <FactValue>{gradeLabel}</FactValue>
            </Fact>
            <Fact>
              <FactKey>Curriculum</FactKey>
              <FactValue>{marketing.browseShortLabel}</FactValue>
            </Fact>
          </Facts>
        </LessonHeroText>

        <Shot>
          <ShotLabel>⎙ What students see</ShotLabel>
          <ShotFrame>
            <PhonicsSlidePreview rule={rule} />
          </ShotFrame>
          <ShotCaption>
            The Curipod slide where students are prompted to write as many words as they
            can with the target sound <strong>{rule}</strong>.
          </ShotCaption>
        </Shot>
      </LessonHero>

      <SectionWhite>
        <About>
          <AboutH2>About this lesson.</AboutH2>
          <AboutP>
            This activity pairs with {curriculum.displayName} {unitLessonLabel}, which
            focuses on <strong>{rule}</strong>. The curriculum teaches the sound through
            letter-sound correspondence and guided word reading; this Curipod activity is
            the productive application step: students generate and spell their own words
            with <strong>{rule}</strong>, getting feedback in real time.
          </AboutP>
          <AboutP>
            The activity surfaces gaps fast. A student who can read a word with{' '}
            <strong>{rule}</strong> when the teacher shows it isn&apos;t necessarily able
            to produce one from scratch — even fewer can produce three or four without
            prompting. The &ldquo;spell as many as you can&rdquo; structure makes that gap
            visible, and the class-wide view tells you who has the pattern and who&apos;s
            stuck.
          </AboutP>
          <AboutP>
            The activity accepts any word that matches the target pattern. Students see
            green confirmation for words that match, and a gentle prompt for words that
            don&apos;t — so misconceptions get surfaced and corrected without singling
            anyone out.
          </AboutP>
          {objective.exampleWords && objective.exampleWords.length > 0 && (
            <WordsBlock>
              <WordsBlockLabel>Example target words</WordsBlockLabel>
              <WordChips>
                {objective.exampleWords.map(word => (
                  <WordChip key={word}>{word}</WordChip>
                ))}
              </WordChips>
            </WordsBlock>
          )}
        </About>
      </SectionWhite>

      <Section>
        <Wrap>
          <SecHead>
            <Eyebrow>How it runs</Eyebrow>
            <SecH2>Four steps. About 10 minutes.</SecH2>
            <SecLead>
              Designed to slot in right after {curriculum.displayName}
              &apos;s direct instruction on <strong>{rule}</strong>.
            </SecLead>
          </SecHead>
          <Steps>
            <Step>
              <StepNum>1</StepNum>
              <StepH3>Hear the sound</StepH3>
              <StepP>
                You introduce <strong>{rule}</strong> from the {curriculum.displayName}{' '}
                lesson. Students listen and identify.
              </StepP>
            </Step>
            <Step>
              <StepNum>2</StepNum>
              <StepH3>Spell &amp; get feedback</StepH3>
              <StepP>
                Every student types words with <strong>{rule}</strong>. Each one gets
                instant feedback and a score.
              </StepP>
            </Step>
            <Step>
              <StepNum>3</StepNum>
              <StepH3>Read aloud</StepH3>
              <StepP>Students share their words with a partner or the class.</StepP>
            </Step>
            <Step>
              <StepNum>4</StepNum>
              <StepH3>Learn &amp; try again</StepH3>
              <StepP>
                You see who got it. Students hear new words from peers — then try again.
              </StepP>
            </Step>
          </Steps>
        </Wrap>
      </Section>

      <SectionWhite>
        <Wrap>
          <SeqHead>
            <Eyebrow>Continue the {curriculum.displayName} sequence</Eyebrow>
          </SeqHead>
          <SeqGrid>
            <Link href={prev.href} passHref>
              <SeqCard $direction="prev">
                <SeqDir>← Previous lesson</SeqDir>
                {prev.location && <SeqLoc>{prev.location}</SeqLoc>}
                <SeqTitle>{prev.title}</SeqTitle>
              </SeqCard>
            </Link>
            <Link href={next.href} passHref>
              <SeqCard $direction="next">
                <SeqDir>Next lesson →</SeqDir>
                {next.location && <SeqLoc>{next.location}</SeqLoc>}
                <SeqTitle>{next.title}</SeqTitle>
              </SeqCard>
            </Link>
          </SeqGrid>
        </Wrap>
      </SectionWhite>

      <Cta>
        <CtaBlob $position="left" />
        <CtaBlob $position="right" />
        <CtaH2>Ready to run this with your class tomorrow?</CtaH2>
        <CtaP>Free to try — sign up and run this lesson with your class today.</CtaP>
        <CtaRow>
          <CtaPrimary as="a" href={lessonRedirect} onClick={handleUseThisLesson}>
            <PlayDot $onOrange>▶</PlayDot>Use this lesson
          </CtaPrimary>
        </CtaRow>
      </Cta>
      {authGuardComponent}
    </LandingPageLayout>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  const paths: { params: { curriculum: string; targetSound: string } }[] = [];
  for (const c of CURRICULA) {
    for (const obj of getObjectivesForCurriculum(c.value)) {
      paths.push({ params: { curriculum: c.slug, targetSound: obj.slug } });
    }
  }
  return { paths, fallback: false };
};

export const getStaticProps: GetStaticProps<Props> = async ctx => {
  const curriculumSlug = ctx.params?.curriculum as string;
  const targetSoundSlug = ctx.params?.targetSound as string;

  const curriculum = getCurriculumBySlug(curriculumSlug);
  if (!curriculum) return { notFound: true };

  const marketing = CURRICULUM_MARKETING[curriculum.value];
  if (!marketing) return { notFound: true };

  const parsed = parseObjectiveSlug(targetSoundSlug);
  if (!parsed) return { notFound: true };

  const objective = getObjectiveById(curriculum.value, parsed.id);
  if (!objective) return { notFound: true };

  const { prev, next } = getAdjacentObjectives(curriculum.value, objective.id);
  const curriculumRoot = `/phonics-practice/${curriculum.slug}`;

  const prevLink: AdjacentLink = prev
    ? {
        href: `${curriculumRoot}/${prev.slug}`,
        location: locationLine(prev),
        title: prev.value,
      }
    : {
        href: `${curriculumRoot}#browse`,
        location: null,
        title: `Back to all ${curriculum.displayName} lessons`,
      };
  const nextLink: AdjacentLink = next
    ? {
        href: `${curriculumRoot}/${next.slug}`,
        location: locationLine(next),
        title: next.value,
      }
    : {
        href: `${curriculumRoot}#browse`,
        location: null,
        title: `Back to all ${curriculum.displayName} lessons`,
      };

  return {
    props: ({
      curriculum,
      marketing,
      objective: { ...objective, unitNumber: objective.unitNumber ?? null },
      rule: extractRuleFromValue(objective.value),
      prev: prevLink,
      next: nextLink,
      _tolgeeSSR: {
        language: DefaultLocale,
      },
    } as unknown) as Props,
  };
};

function locationLine(obj: LandingObjective): string {
  const grade = obj.grade;
  const parts = [grade];
  if (obj.unitNumber !== undefined) parts.push(`Unit ${obj.unitNumber}`);
  parts.push(`Lesson ${obj.lessonNumber}`);
  return parts.join(' · ');
}

function gradeShortLabel(grade: string): string {
  // Used in the breadcrumb meta line and lesson location strings —
  // numeric grades get a "Grade " prefix ("Grade 2 · Unit 2 · Lesson 3"),
  // K becomes "Kindergarten", Pre-K stays "Pre-K", and range markers
  // (e.g. "K–5" from shared-array curricula) pass through unchanged.
  if (grade === 'K') return 'Kindergarten';
  if (grade === 'Pre-K') return 'Pre-K';
  if (/^[1-9]$|^1[0-2]$/.test(grade)) return `Grade ${grade}`;
  return grade;
}

export default ObjectivePhonicsPage;

/* ============================ styled ============================ */

const LessonHero = styled.section`
  max-width: 1160px;
  margin: 0 auto;
  padding: 56px 32px 76px;
  display: grid;
  grid-template-columns: 1.05fr 0.95fr;
  gap: 58px;
  align-items: center;
  background: ${theme.colors.background};

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    gap: 36px;
    padding: 40px 22px 52px;
  }
`;

const LessonHeroText = styled.div``;

const HeroMeta = styled.div`
  font-size: 13px;
  font-weight: 800;
  color: rgba(40, 47, 51, 0.5);
  margin-bottom: 18px;
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;

  a {
    color: inherit;
    text-decoration: none;
  }
  a:hover {
    color: ${theme.colors.orangeDark};
  }
  span[aria-hidden] {
    opacity: 0.5;
  }
`;

const HeroTags = styled.div`
  display: flex;
  gap: 9px;
  flex-wrap: wrap;
  margin-bottom: 18px;
`;

const Tag = styled.span<{ $variant?: 'grade' }>`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: #fff;
  box-shadow: 0 1.6px 3.6px rgba(0, 0, 0, 0.13), 0 0.3px 0.9px rgba(0, 0, 0, 0.11);
  border-radius: 999px;
  padding: 6px 14px;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ $variant }) =>
    $variant === 'grade' ? theme.colors.orangeDark : theme.colors.black};
`;

const ClockDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 999px;
  background: ${theme.colors.successText};
`;

const HeroH1 = styled.h1`
  font-size: clamp(34px, 4.6vw, 56px);
  line-height: 1.02;
  font-weight: 900;
  letter-spacing: -0.03em;
  margin: 0 0 20px;
  color: ${theme.colors.black};
  text-wrap: balance;

  @media (max-width: 992px) {
    font-size: clamp(28px, 7vw, 40px);
  }
`;

const HeroH1Suffix = styled.span`
  color: ${theme.colors.orangeDark};
`;

const HeroLede = styled.p`
  font-size: 18px;
  font-weight: 700;
  color: rgba(40, 47, 51, 0.7);
  line-height: 1.6;
  max-width: 540px;
  margin: 0 0 28px;

  strong {
    color: ${theme.colors.orangeDark};
    font-weight: 700;
  }
`;

const UseButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: ${theme.colors.orange};
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 900;
  padding: 14px 24px;
  cursor: pointer;
  text-decoration: none;
  white-space: nowrap;
  box-shadow: 0 8px 20px -8px rgba(229, 131, 50, 0.6);
  transition: transform 200ms ease, background-color 200ms ease;

  &:hover {
    background: ${theme.colors.orangeDark};
    transform: translateY(-1px);
  }
`;

const PlayDot = styled.span<{ $onOrange?: boolean }>`
  width: 22px;
  height: 22px;
  border-radius: 999px;
  background: ${({ $onOrange }) =>
    $onOrange ? 'rgba(229,131,50,0.2)' : 'rgba(255, 255, 255, 0.25)'};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
`;

const Facts = styled.div`
  display: inline-flex;
  margin-top: 32px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 2px 20px 0 rgba(222, 175, 141, 0.2);
  padding: 4px;

  @media (max-width: 600px) {
    flex-direction: column;
    width: 100%;
  }
`;

const Fact = styled.div`
  padding: 14px 24px;

  & + & {
    border-left: 1px solid rgba(40, 47, 51, 0.1);
  }

  @media (max-width: 600px) {
    & + & {
      border-left: none;
      border-top: 1px solid rgba(40, 47, 51, 0.1);
    }
  }
`;

const FactKey = styled.div`
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: rgba(40, 47, 51, 0.5);
  margin-bottom: 6px;
`;

const FactValue = styled.div`
  font-weight: 900;
  font-size: 17px;
  color: ${theme.colors.black};
`;

const Shot = styled.div`
  position: relative;
`;

const ShotLabel = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${theme.colors.orangeDark};
  margin-bottom: 14px;
`;

const ShotFrame = styled.div`
  background: #fff;
  border-radius: 20px;
  box-shadow: 0 28px 60px -24px rgba(40, 47, 51, 0.28);
  padding: 14px;
`;

const ShotCaption = styled.p`
  font-size: 13px;
  font-weight: 700;
  color: rgba(40, 47, 51, 0.7);
  line-height: 1.5;
  margin: 14px 4px 0;

  strong {
    color: ${theme.colors.black};
    font-weight: 800;
  }
`;

const Section = styled.section`
  padding: 96px 32px;
  background: ${theme.colors.paleBeige};

  @media (max-width: 600px) {
    padding: 64px 22px;
  }
`;

const SectionWhite = styled(Section)`
  background: #fff;
`;

const Wrap = styled.div`
  max-width: 1120px;
  margin: 0 auto;
`;

const About = styled.div`
  max-width: 820px;
  margin: 0 auto;
`;

const Eyebrow = styled.div`
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${theme.colors.orangeDark};
`;

const AboutH2 = styled.h2`
  font-size: clamp(28px, 3.4vw, 40px);
  font-weight: 900;
  letter-spacing: -0.02em;
  line-height: 1.08;
  margin: 14px 0 24px;
  color: ${theme.colors.black};
`;

const AboutP = styled.p`
  font-size: 17px;
  font-weight: 700;
  color: rgba(40, 47, 51, 0.7);
  line-height: 1.7;
  margin: 0 0 20px;

  strong {
    color: ${theme.colors.black};
    font-weight: 800;
  }

  &:last-of-type {
    margin-bottom: 0;
  }
`;

const WordsBlock = styled.div`
  background: ${theme.colors.paleBeige};
  border-radius: 18px;
  padding: 28px 30px;
  margin: 32px 0 0;

  @media (max-width: 600px) {
    padding: 22px 20px;
  }
`;

const WordsBlockLabel = styled.div`
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: rgba(40, 47, 51, 0.55);
  margin-bottom: 16px;
`;

const WordChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const WordChip = styled.span`
  font-weight: 900;
  font-size: 17px;
  color: ${theme.colors.black};
  background: #fff;
  box-shadow: 0 1.6px 3.6px rgba(0, 0, 0, 0.13), 0 0.3px 0.9px rgba(0, 0, 0, 0.11);
  border-radius: 10px;
  padding: 9px 16px;
`;

const SecHead = styled.div`
  text-align: center;
  max-width: 760px;
  margin: 0 auto 56px;
`;

const SecH2 = styled.h2`
  font-size: clamp(28px, 3.4vw, 40px);
  font-weight: 900;
  letter-spacing: -0.02em;
  line-height: 1.08;
  margin: 14px 0;
  color: ${theme.colors.black};
  text-wrap: balance;
`;

const SecLead = styled.p`
  font-size: 18px;
  font-weight: 700;
  color: rgba(40, 47, 51, 0.7);
  line-height: 1.5;
  margin: 0;

  strong {
    color: ${theme.colors.orangeDark};
    font-weight: 700;
  }
`;

const Steps = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 22px;

  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const Step = styled.div`
  background: ${theme.colors.paleBeige};
  background: #fff;
  border-radius: 18px;
  padding: 26px 22px 24px;
  border: 1px solid rgba(40, 47, 51, 0.04);
  transition: transform 200ms ease, box-shadow 200ms ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 2px 20px 0 rgba(222, 175, 141, 0.2);
  }
`;

const StepNum = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 11px;
  background: ${theme.colors.black};
  color: #fff;
  font-weight: 900;
  font-size: 19px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 18px;
`;

const StepH3 = styled.h3`
  font-size: 20px;
  font-weight: 900;
  margin: 0 0 8px;
  letter-spacing: -0.01em;
  color: ${theme.colors.black};
`;

const StepP = styled.p`
  font-size: 14px;
  font-weight: 700;
  color: rgba(40, 47, 51, 0.7);
  line-height: 1.5;
  margin: 0;

  strong {
    color: ${theme.colors.black};
    font-weight: 800;
  }
`;

const SeqHead = styled.div`
  text-align: center;
  margin-bottom: 36px;
`;

const SeqGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  max-width: 920px;
  margin: 0 auto;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const SeqCard = styled.a<{ $direction: 'prev' | 'next' }>`
  background: #fff;
  border-radius: 16px;
  padding: 24px 26px;
  box-shadow: 0 2px 20px 0 rgba(222, 175, 141, 0.2);
  border: 1px solid rgba(40, 47, 51, 0.04);
  text-decoration: none;
  color: ${theme.colors.black};
  display: block;
  transition: transform 200ms ease, box-shadow 200ms ease;
  text-align: ${({ $direction }) => ($direction === 'next' ? 'right' : 'left')};

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(40, 47, 51, 0.14);
  }

  @media (max-width: 600px) {
    text-align: left;
  }
`;

const SeqDir = styled.div`
  font-size: 13px;
  font-weight: 900;
  color: ${theme.colors.orangeDark};
  margin-bottom: 12px;
`;

const SeqLoc = styled.div`
  font-size: 12px;
  font-weight: 800;
  color: rgba(40, 47, 51, 0.5);
  margin-bottom: 6px;
`;

const SeqTitle = styled.div`
  font-size: 20px;
  font-weight: 900;
  letter-spacing: -0.01em;
  color: ${theme.colors.black};
  line-height: 1.2;
`;

const Cta = styled.section`
  background: ${theme.colors.orange};
  color: #fff;
  padding: 92px 32px;
  text-align: center;
  position: relative;
  overflow: hidden;
`;

const CtaBlob = styled.span<{ $position: 'left' | 'right' }>`
  position: absolute;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  ${({ $position }) =>
    $position === 'left'
      ? 'width: 340px; height: 340px; top: -120px; left: -90px;'
      : 'width: 260px; height: 260px; bottom: -110px; right: -60px;'}
`;

const CtaH2 = styled.h2`
  font-size: clamp(30px, 4vw, 46px);
  font-weight: 900;
  letter-spacing: -0.02em;
  line-height: 1.05;
  margin: 0 0 14px;
  color: #fff;
  text-wrap: balance;
  position: relative;
`;

const CtaP = styled.p`
  font-size: 18px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.92);
  max-width: 560px;
  margin: 0 auto 30px;
  line-height: 1.5;
  position: relative;
`;

const CtaRow = styled.div`
  display: flex;
  gap: 14px;
  justify-content: center;
  flex-wrap: wrap;
  position: relative;
`;

const CtaPrimary = styled(UseButton)`
  background: #fff;
  color: ${theme.colors.orangeDark};
  box-shadow: 0 12px 28px -10px rgba(0, 0, 0, 0.3);

  &:hover {
    background: ${theme.colors.black};
    color: #fff;
  }
`;
